import { useQuery, UseQueryResult } from "@tanstack/react-query";
import api, { ApiCatalog, RestApiOptions } from "@/api";
import { IStandardResponse } from "@/search/Langston/types/IStandard";

interface UseStandardsMetadataQueryProps {
  page?: number;
  page_size?: number;
  standard_needle?: string;
  metadata_ids?: string[];
  classroomGradeLevels?: string[];
  skip?: boolean;
  subjects?: string[];
  adoption_years?: number[];
}

const oneMinuteInMilliseconds = 60000;

export const useMetadataSearchQuery = ({
  page,
  page_size,
  standard_needle,
  metadata_ids,
  classroomGradeLevels = [],
  skip = false,
  subjects,
  adoption_years,
}: UseStandardsMetadataQueryProps): UseQueryResult<
  IStandardResponse,
  Error
> => {
  const queryParams = buildQueryParams({
    page,
    page_size,
    ...(standard_needle ? { needle: standard_needle } : {}),
    ...(classroomGradeLevels.length
      ? { grade_levels: classroomGradeLevels }
      : {}),
    metadata_type: "STANDARD",
    metadata_ids,
    ...(subjects?.length ? { subjects } : {}),
    ...(adoption_years?.length ? { adoption_years } : {}),
  });

  const apiOptions: RestApiOptions = {
    timeout: oneMinuteInMilliseconds,
  };

  return useQuery<IStandardResponse, Error>({
    queryKey: [ApiCatalog.metadataSearch.key, queryParams.toString()],
    enabled: !skip,
    queryFn: () => {
      return api(apiOptions).get<IStandardResponse>(
        `${ApiCatalog.metadataSearch.endpoint}?${queryParams.toString()}`,
      );
    },
  });
};

const buildQueryParams = (
  params: Record<
    string,
    string | number | boolean | (string | number | boolean)[] | undefined | null
  >,
): URLSearchParams => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        searchParams.append(key, String(item));
      });
    } else {
      searchParams.append(key, String(value));
    }
  });

  return searchParams;
};

export default useMetadataSearchQuery;
